(() => {
  const SECTION_WRAPPER = document.querySelector('[data-section-type="collection"]');
  if (!SECTION_WRAPPER) return;

  const SECTION_ID = SECTION_WRAPPER.getAttribute('data-section-id');

  // selectors inside the section
  const GRID_SEL        = '#CollectionLoop';
  const SIDEBAR_SEL     = '#filter-groups';
  const PAGINATION_SEL  = '.pagination-custom';
  const PAGINATION_FALLBACK_SEL = '.pagination';

  const PERSISTED_CLASSES = ['expanded', 'drawer--animated'];
  const REMOVED_ON_REFRESH_CLASSES = ['no-mobile-animation'];

  function replaceElementContent(target, source) {
    if (!target || !source) return;

    const classesToRestore = PERSISTED_CLASSES.filter((cls) => target.classList.contains(cls));

    Array.from(target.attributes).forEach((attr) => {
      target.removeAttribute(attr.name);
    });

    Array.from(source.attributes).forEach((attr) => {
      target.setAttribute(attr.name, attr.value);
    });
    if (isMobileView()) {
      [...PERSISTED_CLASSES, ...REMOVED_ON_REFRESH_CLASSES].forEach((cls) => {
        target.classList.remove(cls);
      });
    } else {
      REMOVED_ON_REFRESH_CLASSES.forEach((cls) => target.classList.remove(cls));
    }
    classesToRestore.forEach((cls) => target.classList.add(cls));

    target.innerHTML = '';
    while (source.firstChild) {
      target.appendChild(source.firstChild);
    }
  }

  function isMobileView() {
    if (typeof window.theme?.isMobile === 'function') {
      return window.theme.isMobile();
    }
    return window.innerWidth < 750;
  }

  function getToggleButton() {
    return document.querySelector('[data-aria-toggle]');
  }

  function openMobileSidebar(sidebar, toggleButton = getToggleButton()) {
    if (!sidebar || !isMobileView()) return;

    sidebar.classList.remove('no-mobile-animation');
    sidebar.classList.add('drawer--animated', 'expanded');

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'true');
    }

    const closeEl = sidebar.querySelector('[data-collection-sidebar-close]');
    const isSlideOut = sidebar.hasAttribute('data-collection-sidebar-slide-out');

    if (isSlideOut && window.theme?.a11y?.trapFocus) {
      window.theme.a11y.trapFocus(sidebar, { elementToFocus: closeEl || sidebar });
    }

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
  }

  function closeMobileSidebar(sidebar, toggleButton = getToggleButton()) {
    if (!sidebar || !isMobileView()) return;

    sidebar.classList.remove('expanded');

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
    }

    if (window.theme?.a11y?.removeTrapFocus) {
      window.theme.a11y.removeTrapFocus();
    }

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  function resetSidebarInitialState() {
    if (!isMobileView()) return;
    const sidebar = document.querySelector(SIDEBAR_SEL);
    if (!sidebar) return;
    const toggle = getToggleButton();

    sidebar.classList.remove('expanded', 'drawer--animated', 'no-mobile-animation');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function restoreSidebarState(previousState = {}) {
    const sidebar = document.querySelector(SIDEBAR_SEL);
    const toggleButton = document.querySelector('[data-aria-toggle]');
    if (!sidebar) return;

    const isMobile = isMobileView();
    const ariaExpanded = toggleButton ? toggleButton.getAttribute('aria-expanded') === 'true' : false;
    const shouldExpand = isMobile && (previousState.wasExpanded || ariaExpanded);

    if (!isMobile) {
      if (previousState.wasExpanded) {
        sidebar.classList.add('expanded');
      } else {
        sidebar.classList.remove('expanded');
      }
      return;
    }

    if (shouldExpand) {
      openMobileSidebar(sidebar, toggleButton);
    } else {
      closeMobileSidebar(sidebar, toggleButton);
    }
  }

  function findPagination(root) {
    if (!root) return null;
    return root.querySelector(PAGINATION_SEL) || root.querySelector(PAGINATION_FALLBACK_SEL);
  }

  function buildSectionsUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set('sections', SECTION_ID); // ask Shopify for this section only
    return url.toString();
  }

  function mergePaginationUrl(paginationHref) {
    // Start with current URL to preserve all filter parameters
    const currentUrl = new URL(window.location.href);
    const paginationUrl = new URL(paginationHref, window.location.href);
    
    // Extract page number from pagination link
    const pageNumber = paginationUrl.searchParams.get('page');
    
    // Update page parameter in current URL
    if (pageNumber) {
      currentUrl.searchParams.set('page', pageNumber);
    } else {
      currentUrl.searchParams.delete('page');
    }
    
    // Preserve pathname from pagination link (in case it's different)
    currentUrl.pathname = paginationUrl.pathname;
    
    return currentUrl.toString();
  }

  function updatePaginationActiveState(nextUrl) {
    const target = new URL(nextUrl, window.location.href);
    const pageParam = target.searchParams.get('page');
    const targetPage = pageParam ? String(parseInt(pageParam, 10)) : '1';

    document.querySelectorAll('.pagination-custom').forEach((nav) => {
      nav.querySelectorAll('.pagination-custom__page--active').forEach((el) => {
        el.classList.remove('pagination-custom__page--active');
        el.removeAttribute('aria-current');
      });

      const candidates = Array.from(nav.querySelectorAll('.pagination-custom__page'));
      const match = candidates.find((el) => el.textContent.trim() === targetPage);
      if (match) {
        match.classList.add('pagination-custom__page--active');
        match.setAttribute('aria-current', 'page');
      }
    });
  }

  async function renderFromUrl(nextUrl) {
    const res = await fetch(buildSectionsUrl(nextUrl), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) return;

    // Check if this is a pagination change or filter change
    const urlObj = new URL(nextUrl, window.location.origin);
    const isPaginationChange = urlObj.searchParams.has('page');
    
    // Preserve scroll position for filter changes (not pagination)
    let scrollPosition = null;
    let gridTopOffset = null;
    if (!isPaginationChange) {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      // Capture the grid element's position relative to the viewport before DOM replacement
      const gridElement = document.querySelector(GRID_SEL);
      if (gridElement) {
        const gridRect = gridElement.getBoundingClientRect();
        // Store the grid's top position relative to document
        gridTopOffset = gridRect.top + scrollPosition;
      }
    }

    // JSON object: { "<SECTION_ID>": "<html>" }
    const html = (await res.json())[SECTION_ID];

    // Parse the section HTML we got back
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Find fresh parts inside the returned section
    const freshGrid       = tmp.querySelector(GRID_SEL);
    const freshSidebar    = tmp.querySelector(SIDEBAR_SEL);
    const freshPagination = findPagination(tmp);

    // Find current parts on the live page
    const currentGrid       = document.querySelector(GRID_SEL);
    const currentSidebar    = document.querySelector(SIDEBAR_SEL);
    const currentPagination = findPagination(SECTION_WRAPPER);

    const previousSidebarState = {
      wasExpanded: !!currentSidebar && currentSidebar.classList.contains('expanded'),
    };

    if (freshGrid && currentGrid) currentGrid.replaceWith(freshGrid);
    if (freshPagination || currentPagination) {
      if (freshPagination && currentPagination) {
        currentPagination.replaceWith(freshPagination);
      } else if (freshPagination && !currentPagination) {
        const gridOuter = SECTION_WRAPPER.querySelector('[data-products-grid]');
        const gridLoader = gridOuter ? gridOuter.querySelector('.grid__loader') : null;
        if (gridLoader) {
          gridLoader.before(freshPagination);
        } else if (gridOuter) {
          gridOuter.appendChild(freshPagination);
        } else {
          SECTION_WRAPPER.appendChild(freshPagination);
        }
      } else if (!freshPagination && currentPagination) {
        currentPagination.remove();
      }
    }
    if (freshSidebar && currentSidebar) replaceElementContent(currentSidebar, freshSidebar);
    updatePaginationActiveState(nextUrl);
    restoreSidebarState(previousSidebarState);

    // Clean the URL: keep filters/sort/UTMs, drop sections=
    const clean = nextUrl.replace(/([?&])sections=[^&]+&?/, '$1').replace(/[?&]$/, '');
    history.replaceState(null, '', clean);

    // Re-bind any handlers that live inside the sidebar/grid (because we replaced them)
    attachFilterHandlers();
    window.dispatchEvent(new CustomEvent('theme:filter:update'));

    // Handle scroll position based on change type
    if (isPaginationChange) {
      // Scroll to top instantly (no animation) for pagination changes
      // Jump directly to top with no visible animation - use multiple methods to ensure instant
      requestAnimationFrame(() => {
        // Disable any scroll behavior temporarily
        const html = document.documentElement;
        const body = document.body;
        
        // Set scroll position directly (instant, no animation)
        html.style.scrollBehavior = 'auto';
        body.style.scrollBehavior = 'auto';
        
        html.scrollTop = 0;
        body.scrollTop = 0;
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        
        // Force immediate scroll
        html.scrollTop = 0;
        body.scrollTop = 0;
      });
    } else {
      // Preserve scroll position for filter changes to prevent jarring repositioning
      requestAnimationFrame(() => {
        if (gridTopOffset !== null && scrollPosition !== null) {
          // Try to maintain relative position to the grid element
          const newGridElement = document.querySelector(GRID_SEL);
          if (newGridElement) {
            const newGridRect = newGridElement.getBoundingClientRect();
            const newGridTop = newGridRect.top + (window.pageYOffset || document.documentElement.scrollTop);
            // Calculate the difference in grid position and adjust scroll
            const gridPositionDiff = newGridTop - gridTopOffset;
            const newScrollPosition = scrollPosition + gridPositionDiff;
            window.scrollTo({ top: Math.max(0, newScrollPosition), behavior: 'auto' });
          }
        } else if (scrollPosition !== null) {
          // Fallback: restore absolute scroll position
          window.scrollTo({ top: scrollPosition, behavior: 'auto' });
        }
      });
    }
  }

  function nextUrlFromForm(form) {
    const url = new URL(window.location.href);
    const fd  = new FormData(form);

    // Get all possible filter param names from all form inputs (not just checked ones)
    const allFilterParams = new Set();
    form.querySelectorAll('input[name^="filter."]').forEach(input => {
      allFilterParams.add(input.name);
    });

    // Remove all existing filter params that could be controlled by this form
    allFilterParams.forEach(paramName => {
      url.searchParams.delete(paramName);
    });

    // Add back only the checked/selected values from FormData
    for (const [k, v] of fd.entries()) {
      if (v) url.searchParams.append(k, v);
    }

    // Reset pagination on filter change
    url.searchParams.delete('page');
    return url.toString();
  }

  function attachFilterHandlers() {
    const form = document.querySelector('[data-collection-filters-form]');
    if (!form) return;
    
    // Use a data attribute instead of a property to survive DOM replacement
    if (form.dataset.bound === 'true') return;
    form.dataset.bound = 'true';

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      renderFromUrl(nextUrlFromForm(form));
    });

    form.addEventListener('change', (e) => {
      // Ensure we're working with the current form, not a stale reference
      const currentForm = document.querySelector('[data-collection-filters-form]');
      if (!currentForm) return;
      
      // tiny debounce to avoid double fires
      clearTimeout(currentForm._t);
      currentForm._t = setTimeout(() => renderFromUrl(nextUrlFromForm(currentForm)), 60);
    });

    // Intercept pagination clicks so they also use section rendering
    // Use a global flag to prevent duplicate listeners
    if (!window._paginationHandlerAttached) {
      document.addEventListener('click', (e) => {
        const a = e.target.closest('.pagination-custom a, .pagination a');
        if (!a) return;
        e.preventDefault();
        // Use AJAX rendering for pagination to maintain filtering UX
        renderFromUrl(mergePaginationUrl(a.href));
      }, { capture: true });
      window._paginationHandlerAttached = true;
    }

    if (!window._collectionFilterToggleAttached) {
      document.addEventListener('click', (e) => {
        const sidebar = document.querySelector(SIDEBAR_SEL);
        if (!sidebar) return;

        const toggle = e.target.closest('[data-aria-toggle]');

        if (toggle) {
          openMobileSidebar(sidebar, toggle);
          return;
        }

        const closeTrigger = e.target.closest('[data-collection-sidebar-close]');
        if (closeTrigger) {
          closeMobileSidebar(sidebar);
          return;
        }

        if (!sidebar.classList.contains('expanded')) return;

        const clickedInsideSidebar = e.target.closest(SIDEBAR_SEL);
        if (clickedInsideSidebar) return;

        closeMobileSidebar(sidebar);
      });
      window._collectionFilterToggleAttached = true;
    }

    if (!window._collectionSidebarInitialised) {
      resetSidebarInitialState();
      window._collectionSidebarInitialised = true;
    }
  }

  // initial bind
  attachFilterHandlers();
})();